import { validServiceUniqueNames, type ServiceUniqueName } from '@bv/api/user';
import { cleanRedirectPath } from '@bv/utils/url/clean-redirect-path';

import type { LocationQueryValue } from 'vue-router';

/**
 * Safely extracts the first string value from a Vue Router query parameter.
 *
 * `route.query.x` is typed `LocationQueryValue | LocationQueryValue[]`
 * (`LocationQueryValue = string | null`). When a param is repeated
 * (`?x=a&x=b`) Vue Router gives you an **array**, so `as string` would
 * silently hand you `["a", "b"]` at runtime. This helper always returns
 * a plain `string | undefined`.
 *
 * @param value - The raw query value from `route.query`.
 * @returns The first string in the value, or `undefined` when the value is
 *   absent, `null`, or an empty / all-null array.
 *
 * @example
 * ```ts
 * // router guard
 * const redirectPath = getQueryString(route.query.redirectPath); // string | undefined
 *
 * // repeated param: ?redirectPath=a&redirectPath=b → "a"
 * const redirectPath = getQueryString(['a', 'b']);               // "a"
 * ```
 */
export const getQueryString = (value: LocationQueryValue | LocationQueryValue[] | undefined): string | undefined =>
  (Array.isArray(value) ? value[0] : value) ?? undefined;

/**
 * Type guard: is `value` one of the known {@link validServiceUniqueNames}?
 */
export const isServiceUniqueName = (value: unknown): value is ServiceUniqueName =>
  typeof value === 'string' && (validServiceUniqueNames as readonly string[]).includes(value);

/**
 * Reads the `redirectFrom` query value and returns it only when it is a known service name.
 *
 * @param value - The raw query value from `route.query.redirectFrom`.
 * @returns A valid {@link ServiceUniqueName}, or `undefined`.
 */
export const getRedirectFrom = (
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): ServiceUniqueName | undefined => {
  const raw = getQueryString(value);
  return isServiceUniqueName(raw) ? raw : undefined;
};

/**
 * Reads the `redirectPath` query value and sanitizes it with {@link cleanRedirectPath}
 * (open-redirect protection + normalization to a path starting with `/`).
 *
 * @param value - The raw query value from `route.query.redirectPath`.
 * @returns A safe, normalized in-app path, or `undefined`.
 */
export const getRedirectPath = (value: LocationQueryValue | LocationQueryValue[] | undefined): string | undefined =>
  cleanRedirectPath(getQueryString(value)) || undefined;
